import { type Vec2, vec } from '@/domain/math';

/**
 * Opening an angle without deforming the figure.
 *
 * ## The report
 *
 * "nó di chuyển nhưng bị loạn hình, các cạnh bị chạy điểm khác, khi di chuyển phải bảo đảm cấu
 * trúc hình cố định như cũ."
 *
 * Measured, setting a 45° angle to 90°:
 *
 *     all three free:  OA 200 → 181.4, OC 198 → 180.7, AC 152 → 256
 *     only C free:     OC 198 → **253.5**
 *
 * The solver was doing exactly what it is for, and it is the wrong tool here. It minimises the
 * *angle error*, not the distance a point travels, and one free point against one residual
 * leaves a whole ray of solutions — so it settles wherever the descent happens to stop, and the
 * arm ends up a different length. Every side that shares those points then changes with it.
 *
 * ## What a person means by "make this 90°"
 *
 * Turn the arms, keep their lengths. That is the one motion that changes the angle and nothing
 * else about the figure, and it is what a hand does with a set square. So the new positions are
 * *constructed* — a rotation about the vertex — rather than searched for, which makes them exact,
 * instantaneous and completely predictable.
 *
 * The constraint is still recorded afterwards, so the angle holds when something else is dragged
 * later. It arrives already satisfied, so the solver has nothing to do and nothing to disturb.
 *
 * ## Exactly one arm turns
 *
 * "tại sao không làm giống cách lấy 1 điểm kéo như vẽ tay" — and that is the rule. Dragging a
 * figure by hand moves **one** point; everything attached follows, and everything else stays
 * exactly where it was.
 *
 * The first version split the difference between both arms, which looked elegant and was wrong
 * in use. Measured on a pentagon with an apex drawn over it: changing one corner from 108° to
 * 90° moved *two* of the five base corners, which changed two other interior angles, changed the
 * lengths of two more edges, and dragged the lines to the apex along with them. Four unasked-for
 * changes to make one asked-for one.
 *
 * So one arm turns and the other does not. With both free the second is taken, which is the one
 * a reader names last and the one the chooser in the panel starts on; the chooser is how the
 * other is asked for. With neither free there is no length-preserving answer — moving the vertex
 * must change both arms — and `null` says so.
 */

export interface AngleArms {
  readonly a: Vec2;
  readonly vertex: Vec2;
  readonly c: Vec2;
}

export interface MovableArms {
  readonly a: boolean;
  readonly c: boolean;
}

/**
 * Where the arms should go for the angle to read `radians`, keeping their lengths.
 *
 * `null` when there is no such motion: a zero-length arm has no direction to turn, and neither
 * arm being free leaves nothing that can turn.
 */
export function openAngle(
  arms: AngleArms,
  movable: MovableArms,
  radians: number
): { readonly a?: Vec2; readonly c?: Vec2 } | null {
  if (!movable.a && !movable.c) return null;

  const toA = vec.subtract(arms.a, arms.vertex);
  const toC = vec.subtract(arms.c, arms.vertex);
  if (vec.isZero(toA) || vec.isZero(toC)) return null;

  /*
   * The *signed* angle from one arm to the other, so the target keeps the side it is already on.
   * Taking the unsigned angle and rotating by the difference flips the figure through its own
   * vertex whenever the arms happen to be ordered the other way round — a reflection, which is
   * not what "make it 90°" asks for.
   */
  const from = Math.atan2(toA.x * toC.y - toA.y * toC.x, toA.x * toC.x + toA.y * toC.y);
  const target = (from < 0 ? -1 : 1) * radians;
  const turn = target - from;

  const spun = (point: Vec2, by: number): Vec2 => {
    const away = vec.subtract(point, arms.vertex);
    const cos = Math.cos(by);
    const sin = Math.sin(by);
    return vec.vec2(
      arms.vertex.x + away.x * cos - away.y * sin,
      arms.vertex.y + away.x * sin + away.y * cos
    );
  };

  // One point moves, as a hand would move it. See the note above.
  return movable.c ? { c: spun(arms.c, turn) } : { a: spun(arms.a, -turn) };
}
